<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Bell, Collection, Grid, Search, Service } from "@element-plus/icons-vue";
import type { NavigationChild, NavigationMenu } from "@wjbk/workbench-contracts";
import { api } from "./api";
import { navigationIcon as icon } from "./navigation-icons";

const route = useRoute();
const router = useRouter();
const title = computed(() => String(route.meta.title ?? "工作台"));
const section = computed(() => String(route.meta.section ?? "工作台"));
const isPublic = computed(() => Boolean(route.meta.public));
const isEmbeddedApp = computed(() => route.path.startsWith("/apps/customer-service") || route.path.startsWith("/apps/store"));
const displayName = ref("当前用户");
const roles = ref<string[]>([]);
const menus = ref<NavigationMenu[]>([]);
const navError = ref("");
const sidebarPinned = ref(false);
const sidebarHovered = ref(false);
const activeLeftId = ref("");
const activeTopId = ref("");
const flyoutTop = ref(76);
const searchInput = ref<HTMLInputElement | null>(null);
const searchQuery = ref("");
const searchOpen = ref(false);
const accountOpen = ref(false);
let closeTimer: number | undefined;

const sortedMenus = computed(() => [...menus.value].sort((a, b) => a.order - b.order));
const leftMenus = computed(() => sortedMenus.value.filter((menu) => menu.placement === "left"));
const topMenus = computed(() => sortedMenus.value.filter((menu) => menu.placement === "top"));
const activeLeft = computed(() => leftMenus.value.find((menu) => menu.id === activeLeftId.value));
const sidebarExpanded = computed(() => sidebarPinned.value || sidebarHovered.value || Boolean(activeLeftId.value));
const searchResults = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase();
  if (!query) return [];
  return sortedMenus.value.flatMap((menu) => menu.children.map((child) => ({ ...child, group: menu.label })))
    .filter((child) => (child.label + " " + child.description + " " + child.group).toLocaleLowerCase().includes(query)).slice(0, 8);
});
const roleLabel = computed(() => roles.value.includes("PLATFORM_SUPERADMIN") ? "平台超级管理员" : (roles.value[0] ?? "工作台成员"));

function activeChild(child: NavigationChild) {
  return child.path === "/" ? route.path === "/" : route.path === child.path || route.path.startsWith(child.path + "/");
}
function activeMenu(menu: NavigationMenu) { return menu.children.some(activeChild); }
function clearClose() { if (closeTimer) window.clearTimeout(closeTimer); closeTimer = undefined; }
function closeLeftSoon() {
  clearClose();
  closeTimer = window.setTimeout(() => { activeLeftId.value = ""; sidebarHovered.value = false; }, 240);
}
function openLeft(menu: NavigationMenu, event: Event) {
  clearClose();
  sidebarHovered.value = true;
  activeLeftId.value = menu.id;
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  flyoutTop.value = Math.max(66, Math.min(rect.top, window.innerHeight - 310));
  activeTopId.value = "";
}
function openTop(id: string) { clearClose(); activeTopId.value = id; activeLeftId.value = ""; }
function closeTopSoon() { clearClose(); closeTimer = window.setTimeout(() => { activeTopId.value = ""; }, 220); }
function onFocusOut(event: FocusEvent, kind: "top" | "left") {
  const next = event.relatedTarget;
  if (next instanceof Node && (event.currentTarget as HTMLElement).contains(next)) return;
  if (kind === "top") closeTopSoon(); else closeLeftSoon();
}
function onSearchFocusOut(event: FocusEvent) {
  const next = event.relatedTarget;
  if (next instanceof Node && (event.currentTarget as HTMLElement).contains(next)) return;
  window.setTimeout(() => { searchOpen.value = false; }, 180);
}
function onAccountFocusOut(event: FocusEvent) {
  const next = event.relatedTarget;
  if (next instanceof Node && (event.currentTarget as HTMLElement).contains(next)) return;
  accountOpen.value = false;
}
function closeOverlays() {
  clearClose(); activeTopId.value = ""; activeLeftId.value = ""; sidebarHovered.value = false;
  searchOpen.value = false; accountOpen.value = false;
}
function onKeydown(event: KeyboardEvent) {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
    event.preventDefault(); searchInput.value?.focus(); searchOpen.value = true;
  }
  if (event.key === "Escape") { closeOverlays(); searchInput.value?.blur(); }
}
function openFirstResult() {
  const first = searchResults.value[0];
  if (first) { void router.push(first.path); closeOverlays(); }
}
async function loadNavigation() {
  try {
    const data = await api.navigation();
    menus.value = data.menus;
    navError.value = "";
  } catch (cause) {
    navError.value = cause instanceof Error ? cause.message : "导航加载失败";
  }
}
async function loadShell() {
  await Promise.all([loadNavigation(), api.me().then((current) => {
    displayName.value = String(current.displayName ?? current.username ?? "当前用户");
    roles.value = Array.isArray(current.roles) ? current.roles.map(String) : [];
  }).catch(() => { /* Expired sessions are redirected by the API layer. */ })]);
}
async function logout() { await api.logout(); location.assign("/login"); }

watch(() => route.fullPath, closeOverlays);
watch(isPublic, (publicNow, wasPublic) => {
  if (wasPublic && !publicNow) void loadShell();
});
onMounted(async () => {
  window.addEventListener("keydown", onKeydown);
  window.addEventListener("wjbk:navigation-updated", loadNavigation);
  if (!isPublic.value) await loadShell();
});
onUnmounted(() => {
  clearClose(); window.removeEventListener("keydown", onKeydown);
  window.removeEventListener("wjbk:navigation-updated", loadNavigation);
});
</script>

<template>
  <RouterView v-if="isPublic" />
  <div v-else class="app-shell" :class="{ 'sidebar-expanded': sidebarExpanded }">
    <header class="topbar">
      <div class="topbar-identity">
        <button class="nav-toggle" type="button" :aria-label="sidebarPinned ? '取消固定导航' : '固定展开导航'" :aria-pressed="sidebarPinned" @click="sidebarPinned = !sidebarPinned">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
        </button>
        <RouterLink class="brand" to="/" aria-label="玩具百科工作台首页">
          <img class="brand-logo" src="/brand/workbench-logo-rounded-v2.png" alt="" />
          <span><strong>玩具百科</strong><small>一体化工作台</small></span>
        </RouterLink>
      </div>

      <nav class="top-business" aria-label="业务系统导航">
        <div v-for="menu in topMenus" :key="menu.id" class="top-business-item"
          @mouseenter="openTop(menu.id)" @mouseleave="closeTopSoon" @focusin="openTop(menu.id)" @focusout="onFocusOut($event, 'top')">
          <button type="button" class="top-business-button" :class="{ active: activeMenu(menu) }"
            :aria-expanded="activeTopId === menu.id" aria-haspopup="menu" @click="activeTopId = activeTopId === menu.id ? '' : menu.id">
            <component :is="icon(menu.icon)" class="nav-svg" /><span>{{ menu.label }}</span><b>⌄</b>
          </button>
          <div v-if="activeTopId === menu.id" class="top-menu-panel" role="menu" @mouseenter="clearClose" @mouseleave="closeTopSoon">
            <div class="top-menu-heading"><strong>{{ menu.label }}</strong><span>{{ menu.description }}</span></div>
            <div class="top-menu-grid">
              <RouterLink v-for="child in menu.children" :key="child.id" :to="child.path" role="menuitem" class="top-menu-link" @click="closeOverlays">
                <span class="top-menu-icon"><component :is="icon(child.icon)" class="nav-svg" /></span>
                <strong>{{ child.label }}</strong><small>{{ child.description }}</small>
              </RouterLink>
            </div>
          </div>
        </div>
      </nav>

      <div class="topbar-actions">
        <div class="global-search" @focusin="searchOpen = true" @focusout="onSearchFocusOut">
          <Search class="search-icon" /><input ref="searchInput" v-model="searchQuery" aria-label="搜索工作台菜单" placeholder="搜索菜单、产品、任务…" @keydown.enter.prevent="openFirstResult" />
          <kbd>⌘ K</kbd>
          <div v-if="searchOpen && searchQuery.trim()" class="search-panel">
            <RouterLink v-for="result in searchResults" :key="result.id" :to="result.path" @click="searchQuery = ''; closeOverlays()">
              <component :is="icon(result.icon)" class="nav-svg" /><span><strong>{{ result.label }}</strong><small>{{ result.group }} · {{ result.description }}</small></span>
            </RouterLink>
            <p v-if="!searchResults.length">没有匹配的菜单</p>
          </div>
        </div>
        <el-dropdown trigger="click">
          <button class="top-icon-button" type="button" title="快捷方式" aria-label="快捷方式"><Grid class="nav-svg" /></button>
          <template #dropdown><el-dropdown-menu>
            <el-dropdown-item><RouterLink to="/">我的工作台</RouterLink></el-dropdown-item>
            <el-dropdown-item><RouterLink to="/expenses/new">支出申请</RouterLink></el-dropdown-item>
            <el-dropdown-item><RouterLink to="/integrations">应用中心</RouterLink></el-dropdown-item>
          </el-dropdown-menu></template>
        </el-dropdown>
        <RouterLink class="top-icon-button secondary-top-icon" to="/knowledge" title="知识手册" aria-label="知识手册"><Collection class="nav-svg" /></RouterLink>
        <RouterLink class="top-icon-button" to="/alerts" title="消息中心" aria-label="消息中心"><Bell class="nav-svg" /><i class="notification-dot"></i></RouterLink>
        <RouterLink class="top-icon-button secondary-top-icon" to="/help" title="帮助中心" aria-label="帮助中心"><Service class="nav-svg" /></RouterLink>
        <div class="account-wrap" @focusout="onAccountFocusOut">
          <button class="account-button" type="button" aria-label="用户菜单" :aria-expanded="accountOpen" @click="accountOpen = !accountOpen">
            <span class="avatar">{{ displayName.slice(0, 1) }}</span><span class="account-name">{{ displayName }}</span><b>⌄</b>
          </button>
          <div v-if="accountOpen" class="account-panel"><strong>{{ displayName }}</strong><small>{{ roleLabel }}</small><button type="button" @click="logout">退出登录</button></div>
        </div>
      </div>
    </header>

    <aside class="sidebar" aria-label="左侧导航" @mouseenter="sidebarHovered = true; clearClose()" @mouseleave="closeLeftSoon" @focusin="sidebarHovered = true; clearClose()" @focusout="onFocusOut($event, 'left')">
      <nav class="side-nav">
        <button v-for="menu in leftMenus" :key="menu.id" type="button" class="side-primary" :class="{ active: activeMenu(menu), hovered: activeLeftId === menu.id }"
          :aria-label="menu.label" :aria-expanded="activeLeftId === menu.id" aria-haspopup="menu"
          @mouseenter="openLeft(menu, $event)" @focus="openLeft(menu, $event)" @click="openLeft(menu, $event)">
          <component :is="icon(menu.icon)" class="nav-svg" /><span>{{ menu.label }}</span><b>›</b>
        </button>
        <p v-if="navError" class="nav-load-error">导航加载失败 <button type="button" @click="loadNavigation">重试</button></p>
      </nav>
      <div class="sidebar-foot"><span class="online-dot"></span><span>测试环境运行中</span></div>
    </aside>

    <div v-if="activeLeft" class="left-flyout" :style="{ top: flyoutTop + 'px' }" role="menu"
      @mouseenter="clearClose" @mouseleave="closeLeftSoon" @focusin="clearClose" @focusout="onFocusOut($event, 'left')">
      <div class="flyout-heading"><strong>{{ activeLeft.label }}</strong><small>{{ activeLeft.description }}</small></div>
      <RouterLink v-for="child in activeLeft.children" :key="child.id" :to="child.path" class="flyout-link" :class="{ active: activeChild(child) }" role="menuitem" @click="closeOverlays">
        <span class="flyout-icon"><component :is="icon(child.icon)" class="nav-svg" /></span>
        <span><strong>{{ child.label }}</strong><small>{{ child.description }}</small></span>
      </RouterLink>
    </div>

    <main class="workspace">
      <div v-if="!isEmbeddedApp" class="page-heading">
        <div><div class="breadcrumb"><span>{{ section }}</span><b>/</b><span>{{ title }}</span></div><h1>{{ title }}</h1></div>
        <div class="environment-pill"><span></span>测试环境</div>
      </div>
      <section class="content" :class="{ 'embedded-app-content': isEmbeddedApp }"><RouterView /></section>
    </main>
  </div>
</template>
